import React from "react";
import { Star } from "lucide-react";

// A tiny, decorative "guest review" strip designed to sit inside each
// hotel card on the homepage. Two ingredients — a cluster of guest
// avatars (overlapping bubbles with a soft amber ring) and a row of
// five stars filled to ~4.5. The stars are purely visual and identical
// across every card: no numeric score, no label. The goal is a
// consistent quality cue that reads at a glance without competing with
// the hotel name, location or CTA typography.
//
// Responsiveness: sized in xs on mobile, bumps one step at `sm`. Ring
// widths and star sizes are tuned so the strip stays legible against
// the dark cinematic gradient at every breakpoint.
//
// Props:
//   testIdPrefix – base for the data-testid (defaults to
//                  "hotel-card-mini-review"). Callers append their own
//                  slug so multiple cards render side-by-side without
//                  colliding on the DOM.
const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=96&h=96&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=96&h=96&q=80",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=96&h=96&q=80",
];

const HotelCardMiniReview = ({ testIdPrefix = "hotel-card-mini-review" }) => (
  <div
    className="mt-3 flex items-center gap-2.5 sm:gap-3"
    data-testid={testIdPrefix}
    // Prevent CMS text-edit mode from ever touching this decorative strip.
    data-no-cms-text="true"
  >
    <div className="flex -space-x-2" aria-hidden="true">
      {AVATARS.map((src, i) => (
        <img
          key={`mini-avatar-${i}`}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover ring-[1.5px] ring-[#D4A373]/75 shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
          style={{ zIndex: AVATARS.length - i }}
        />
      ))}
    </div>
    <div
      className="flex items-center gap-[1px]"
      aria-hidden="true"
      data-testid={`${testIdPrefix}-stars`}
    >
      {/* Four fully filled amber stars */}
      {[0, 1, 2, 3].map((i) => (
        <Star
          key={`mini-full-${i}`}
          className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#D4A373] text-[#D4A373]"
          strokeWidth={1.5}
        />
      ))}
      {/* Fifth star: 50% filled via clip-path overlay so the visual
          reads exactly like a 4.5-star rating without any half-star
          asset dependency. */}
      <span className="relative inline-block w-3 h-3 sm:w-3.5 sm:h-3.5">
        <Star
          className="absolute inset-0 w-full h-full text-[#FDFBF7]/30"
          strokeWidth={1.5}
        />
        <Star
          className="absolute inset-0 w-full h-full fill-[#D4A373] text-[#D4A373]"
          strokeWidth={1.5}
          style={{ clipPath: "inset(0 50% 0 0)" }}
        />
      </span>
    </div>
  </div>
);

export default HotelCardMiniReview;
